import type { SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { Check, CornerLeftUp, GitFork } from 'lucide-react';
import type { TaskDependencyRef, TaskParent } from '@shared/types/tasks';
import { useFormatTaskRef } from '@/lib/current-project';
import { useBoardPath } from '@/navigation/project-paths';
import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/StatusBadge';

/** Stops a press on the link from opening, picking or dragging the card it sits on. */
const stop = (e: SyntheticEvent) => e.stopPropagation();

/**
 * T-71: a synced sub-task whose source board took it off this parent; the factory leaves it here, marked.
 */
export function OrphanedBadge({ className }: { className?: string }) {
  return (
    <StatusBadge tone="warning" size="sm" className={cn('shrink-0', className)} data-hint="Not under this task on its source board any more; left here" aria-label="Orphaned: Not under this task on its source board any more; left here">
      Orphaned
    </StatusBadge>
  );
}

/**
 * T-42: "Sub-task of T-12" for a sub-task, linking to the larger task it's part of. `card`: the Board
 * card's compact form (a sub-task shown on the Board too); detail sits in TaskProperties.
 */
export function SubtaskOf({
  parent,
  variant = 'detail',
  onNavigate,
  className,
}: {
  parent: TaskParent;
  variant?: 'card' | 'detail';
  onNavigate?: () => void;
  className?: string;
}) {
  const boardPath = useBoardPath();
  const formatRef = useFormatTaskRef();
  const card = variant === 'card';
  return (
    <span
      className={cn('inline-flex min-w-0 max-w-full items-center gap-1.5', card ? 'text-xs' : 'text-sm', className)}
      data-hint={`Part of ${formatRef(parent.number)} ${parent.title}`}
    >
      <CornerLeftUp className={cn('shrink-0 text-muted-foreground', card ? 'size-3' : 'size-3.5')} aria-hidden />
      <span className="shrink-0 text-muted-foreground">Sub-task of</span>
      <Link
        to={boardPath(parent.task_id)}
        onClick={(e) => {
          stop(e);
          onNavigate?.();
        }}
        onMouseDown={stop}
        onPointerDown={stop}
        onKeyDown={stop}
        className={cn('min-w-0 truncate hover:underline', card ? 'max-w-[10rem]' : 'max-w-[16rem]')}
      >
        {formatRef(parent.number)}
        {card ? null : ` ${parent.title}`}
      </Link>
      {parent.orphaned ? <OrphanedBadge /> : null}
    </span>
  );
}

/**
 * SF-T-268: "after T-45, T-46": the sub-tasks of the same parent it waits on (task.after), in TaskSubtasks' rows (a
 * button opening its After… picker when it can be changed) and on the Board card of a sub-task shown there too.
 * SF-T-279: with `unlocked` (TaskSubtasks, which knows the other sub-tasks), each one that's ready (isSubtaskUnlocked: its
 * PR is up and its run has finished, or it's done) shows in green with a tick, so it's clear what can go next.
 */
export function SubtaskAfter({ after, onEdit, unlocked }: { after: TaskDependencyRef[]; onEdit?: () => void; unlocked?: (taskId: string) => boolean }) {
  const formatRef = useFormatTaskRef();
  const hint = unlocked
    ? 'Waits on these: built one at a time, it starts once each has a PR or is done. Green: its PR is up and its run has finished'
    : 'Waits on these: built one at a time, it starts once each has a PR or is done';
  const refs = (
    <>
      <GitFork className="size-3.5 shrink-0" aria-hidden />
      <span>
        after{' '}
        {after.map((d, i) => {
          const ready = unlocked?.(d.task_id) ?? false;
          return (
            <span key={d.task_id}>
              {i ? ', ' : null}
              <span className={cn('whitespace-nowrap', ready && 'font-medium text-success-strong')} data-testid={ready ? 'after-ready' : undefined}>
                {ready ? <Check className="mr-0.5 inline size-3 align-[-1px]" aria-hidden /> : null}
                {formatRef(d.number)}
              </span>
              {unlocked ? <span className="sr-only">{ready ? ' (ready)' : ' (not ready)'}</span> : null}
            </span>
          );
        })}
      </span>
    </>
  );
  return onEdit ? (
    <button
      type="button"
      // Phones: a 44px touch target round the line, without making the line taller.
      className="relative inline-flex items-center gap-1 rounded text-left hover:text-foreground hover:underline max-sm:before:absolute max-sm:before:-inset-x-1 max-sm:before:-inset-y-3.5 max-sm:before:content-['']"
      onClick={onEdit}
      data-hint={hint}
    >
      {refs}
    </button>
  ) : (
    <span className="inline-flex items-center gap-1" data-hint={hint}>
      {refs}
    </span>
  );
}
